import {
  type BulkInviteColumn,
  type BulkInviteMappingOverride,
  type BulkInvitePreview,
  type BulkInviteResolvedColumn,
  parseBulkInviteError,
  type ParsedBulkInviteError,
  useBulkInviteRequest,
  useInspectBulkInviteMapping,
  useReuploadBulkInvite,
  useSendBulkInvite,
  useValidateBulkInvite,
} from '@qse/api'
import { type ModalComponentProps } from '@qse/constants'
import { useState } from 'react'

import ColumnMappingStep from './column-mapping-step'
import BulkInviteHistory from './history'
import PreviewPanel from './preview-panel'
import TrackingView from './tracking-view'
import UploadStep from './upload-step'

type Step = 'upload' | 'mapping' | 'preview' | 'tracking'
type View = 'invite' | 'history'

const tabClass = (active: boolean) =>
  `px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
    active
      ? 'border-primary text-primary'
      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
  }`

/**
 * Ship the mapping the admin actually saw — complete, keyed by index, `matchedBy`
 * dropped (it's how we guessed, not what we decided).
 */
const toOverride = (mapping: BulkInviteResolvedColumn[]): BulkInviteMappingOverride => ({
  columns: mapping.map(({ index, field }) => ({ index, field })),
})

/**
 * Bulk user invitation modal. A state machine over the async flow:
 * upload → review column mapping → review breakdown → send (202) → track to terminal.
 *
 * The mapping step is the reason this is four steps rather than two. Its edits are
 * held here as local state and submitted *with* the file on both the full dry run and
 * the send — the server re-parses each time, so sending the complete mapping (not a
 * patch) is what makes the send deterministic: what the admin saw is what runs.
 *
 * Re-upload re-enters the same flow, keeping `reuploadTarget` set; on send it
 * overwrites that request instead of creating a new one, and tracks the same id.
 */
export default function BulkInvite(props: ModalComponentProps) {
  const { setModelActive, onClose, data } = props
  const showHistory = !!data?.data?.showHistory
  // Scopes the history cache per tenant. A super admin viewing a specific tenant
  // passes that tenant here (the same id that drives the x-tenant-id header), so
  // switching tenants and reopening shows the new tenant's history, not a stale
  // cache. Undefined for the default/own-tenant view and for tenant admins.
  const tenantId = data?.data?.tenantId as string | undefined

  const [view, setView] = useState<View>('invite')
  const [step, setStep] = useState<Step>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [columns, setColumns] = useState<BulkInviteColumn[] | null>(null)
  const [mapping, setMapping] = useState<BulkInviteResolvedColumn[]>([])
  const [preview, setPreview] = useState<BulkInvitePreview | null>(null)
  // Rows the admin removed on the Review screen (by file row number). Held here
  // until Send, where they ride along as skipRows and the server excludes them.
  const [deletedRows, setDeletedRows] = useState<Set<number>>(new Set())
  const [requestId, setRequestId] = useState<string | null>(null)
  const [queuedTotal, setQueuedTotal] = useState<number | null>(null)
  const [error, setError] = useState<ParsedBulkInviteError | null>(null)
  /** Set while correcting an existing request — send overwrites it in place. */
  const [reuploadTarget, setReuploadTarget] = useState<string | null>(null)

  const inspectMutation = useInspectBulkInviteMapping()
  const validateMutation = useValidateBulkInvite()
  const sendMutation = useSendBulkInvite()
  const reuploadMutation = useReuploadBulkInvite()

  const { data: trackedRequest, isLoading: isTrackingLoading } = useBulkInviteRequest(
    requestId || undefined,
    { enabled: !!requestId },
  )

  const isSending = sendMutation.isPending || reuploadMutation.isPending

  const handleClose = () => {
    setModelActive(undefined)
    onClose()
  }

  const resetFlow = () => {
    setFile(null)
    setColumns(null)
    setMapping([])
    setPreview(null)
    setDeletedRows(new Set())
    setError(null)
  }

  const handleSelectFile = (selected: File) => {
    setFile(selected)
    setColumns(null)
    setMapping([])
    setPreview(null)
    setDeletedRows(new Set())
    setError(null)
  }

  // Toggle a row's removed state. Deletions are local until Send.
  const handleToggleDelete = (row: number) => {
    setDeletedRows(prev => {
      const next = new Set(prev)

      if (next.has(row)) {
        next.delete(row)
      } else {
        next.add(row)
      }

      return next
    })
  }

  const handleClearFile = () => {
    resetFlow()
  }

  /** Upload → Mapping. The cheap dry run: parse + map only, no DB or Cognito. */
  const handleInspect = async () => {
    if (!file) return
    setError(null)

    try {
      const result = await inspectMutation.mutateAsync(file)

      setColumns(result.columns)
      setMapping(result.mapping)
      setStep('mapping')
    } catch (err) {
      setError(parseBulkInviteError(err))
    }
  }

  /**
   * Mapping → Review. The expensive dry run (a Cognito lookup per email) — this is
   * the only place it runs, which is why the mapping screen edits locally.
   */
  const handleConfirmMapping = async () => {
    if (!file) return
    setError(null)

    try {
      const result = await validateMutation.mutateAsync({ file, mapping: toOverride(mapping) })

      setPreview(result)
      setStep('preview')
    } catch (err) {
      // A structural failure here is about the mapping, so stay on the mapping
      // screen where it can be fixed rather than bouncing back to upload.
      setError(parseBulkInviteError(err))
    }
  }

  const handleSend = async () => {
    if (!file) return
    setError(null)

    try {
      const override = toOverride(mapping)
      const skipRows = [...deletedRows]
      const result = reuploadTarget
        ? await reuploadMutation.mutateAsync({
            requestId: reuploadTarget,
            file,
            mapping: override,
            skipRows,
          })
        : await sendMutation.mutateAsync({ file, mapping: override, skipRows })

      setRequestId(result.requestId)
      setQueuedTotal(result.totalRows)
      setStep('tracking')
    } catch (err) {
      // Stay on Review. Send is only reachable from here, and the mapping and preview
      // are both still valid — bouncing to upload would re-inspect the file on
      // Continue and silently overwrite the admin's column edits.
      setError(parseBulkInviteError(err))
    }
  }

  /** Review → Mapping. Drops any send failure; the mapping screen has its own. */
  const handleBackToMapping = () => {
    setError(null)
    setStep('mapping')
  }

  /** History → correct an existing request. Routes through the same mapping flow. */
  const handleStartReupload = (targetId: string) => {
    resetFlow()
    setReuploadTarget(targetId)
    setRequestId(null)
    setQueuedTotal(null)
    setStep('upload')
    setView('invite')
  }

  const renderInvite = () => {
    if (step === 'tracking') {
      return (
        <TrackingView
          isLoading={isTrackingLoading}
          queuedTotal={queuedTotal}
          request={trackedRequest}
          onClose={handleClose}
        />
      )
    }

    if (step === 'preview' && preview) {
      return (
        <PreviewPanel
          deletedRows={deletedRows}
          error={error}
          fileName={file?.name}
          isSending={isSending}
          preview={preview}
          onBack={handleBackToMapping}
          onSend={handleSend}
          onToggleDelete={handleToggleDelete}
        />
      )
    }

    if (step === 'mapping' && columns) {
      return (
        <ColumnMappingStep
          columns={columns}
          error={error}
          fileName={file?.name}
          isConfirming={validateMutation.isPending}
          mapping={mapping}
          onBack={() => {
            setError(null)
            setStep('upload')
          }}
          onChange={setMapping}
          onConfirm={handleConfirmMapping}
        />
      )
    }

    return (
      <UploadStep
        error={error}
        fileName={file?.name}
        isInspecting={inspectMutation.isPending}
        isReupload={!!reuploadTarget}
        onCancel={handleClose}
        onClearFile={handleClearFile}
        onContinue={handleInspect}
        onFileSelected={handleSelectFile}
      />
    )
  }

  if (!showHistory) {
    return renderInvite()
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex gap-1 border-b border-gray-200 dark:border-[#2A2A2A]">
        <button
          className={tabClass(view === 'invite')}
          type="button"
          onClick={() => setView('invite')}
        >
          Upload
        </button>
        <button
          className={tabClass(view === 'history')}
          type="button"
          onClick={() => setView('history')}
        >
          History
        </button>
      </div>

      {view === 'history' ? (
        <BulkInviteHistory
          enabled={showHistory}
          tenantId={tenantId}
          onReupload={handleStartReupload}
        />
      ) : (
        renderInvite()
      )}
    </div>
  )
}
